// React and Material-UI imports
import React, { PureComponent } from 'react';
import Grid from '@mui/material/Grid';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';

// Material-UI Icons and Components
import { Add as AddIcon } from '@mui/icons-material';
import Chip from '@mui/material/Chip';
import FormHelperText from '@mui/material/FormHelperText';

// External Libraries
import * as dates from '@simplybenefits/sb-dates';

// Custom Components
import TextInput from '../../../components/TextFields/TextInput.js';
import NumberInput from '../../../components/TextFields/NumberInput.js';
import DateInput from '../../../components/TextFields/DateInput.js';
import Modal from '../../../components/Modals/Modal.js';
import StepCompletionNotif from '../../../components/Modals/StepCompletionNotif.js';
import MessageCard from '../../../components/Cards/MessageCard.js';
import Text from '../../../components/Text.js';
import Button from '../../../components/Button.js';
import Checkbox from '../../../components/Checkbox.js';
import { EditBankingDetails } from '../../../index.js';
import DisplayBankingDetails from '../../../components/DisplayBankingDetails.js';
import SurvivorBenefitDependentRow from './SurvivorBenefitDependentRow.js';
import EmployeeNotAtWorkRow from './EmployeeNotAtWorkRow.js';
import ContractEmployeeRow from './ContractEmployeeRow.js';
import ExcludedFromBenefitsRow from './ExcludedFromBenefitsRow.js';
import ExistingCoverageBlock from './ExistingCoverageBlock.js';
import LsaModal from '../../../components/Modals/LsaSelectionModal.js';

// Services and Themes
import * as Services from '../../../services/index.js';
import * as Theme from '../../../theme/index.js';
import colors from '../../../theme/colors.js';

// Localization
import i18n from '../../../localization/i18n.js';
import tokens from '../../../localization/tokens.js';
import * as localizationMaps from '../../../localization/maps.js';
class MasterAppForm extends PureComponent {
  state = {
    company: this.props.data.company || '',
    addressLineOne: this.props.data.addressLineOne || '',
    addressLineTwo: this.props.data.addressLineTwo || '',
    city: this.props.data.city || '',
    province: localizationMaps.fromEnglish('provinces', this.props.data.province) || '',
    postalCode: this.props.data.postalCode || '',
    categories: this.props.data.categories,
    nature: this.props.data.nature || '',
    numLives: this.props.data.numLives || 0,
    hasExistingCoverage: this.props.data.hasExistingCoverage || false,
    existingCoverage: this.props.data.existingCoverage || [],
    numEmployeesInsured: this.props.data.numEmployeesInsured || 0,
    numEmployeesCoveredByWCB: this.props.data.numEmployeesCoveredByWCB || 0,
    dependentsWithSurvivorBenefits: this.props.data.dependentsWithSurvivorBenefits ? this.props.data.dependentsWithSurvivorBenefits.map(dependent => ({
      ...dependent,
      expiryDate: Services.dates.formatIncomingDate(dependent.expiryDate)
    })) : [],
    employeesNotActivelyAtWork: this.props.data.employeesNotActivelyAtWork ? this.props.data.employeesNotActivelyAtWork.map(employee => ({
      ...employee,
      dateLastAtWork: Services.dates.formatIncomingDate(employee.dateLastAtWork),
      expectedReturnDate: Services.dates.formatIncomingDate(employee.expectedReturnDate)
    })) : [],
    contractEmployees: this.props.data.contractEmployees ? this.props.data.contractEmployees.map(employee => ({
      ...employee,
      contractEndDate: Services.dates.formatIncomingDate(employee.contractEndDate),
      contractStartDate: Services.dates.formatIncomingDate(employee.contractStartDate)
    })) : [],
    exclusionsFromCoverage: this.props.data.exclusionsFromCoverage || [],
    stopLoss: {
      status: this.props.data.stopLoss.status || false,
      amount: this.props.data.stopLoss.amount || 0,
      insuredsWithRecurringClaims: this.props.data.stopLoss.insuredsWithRecurringClaims || false
    },
    notes: this.props.data.notes || '',
    signage: {
      advisor: {
        name: this.props.data.signage.advisor.name || '',
        signature: this.props.data.signage.advisor.signature || '',
        acceptedTerms: this.props.data.signage.advisor.acceptedTerms || false,
        timestampOfAcceptance: this.props.data.signage.advisor.timestampOfAcceptance || null,
        datedOn: this.props.data.signage.advisor.datedOn ? Services.dates.formatIncomingDate(this.props.data.signage.advisor.datedOn) : null
      },
      authority: {
        name: this.props.data.signage.authority.name || '',
        signature: this.props.data.signage.authority.signature || '',
        acceptedTerms: this.props.data.signage.authority.acceptedTerms || false,
        timestampOfAcceptance: this.props.data.signage.authority.timestampOfAcceptance || null,
        datedOn: this.props.data.signage.authority.datedOn ? Services.dates.formatIncomingDate(this.props.data.signage.authority.datedOn) : null
      },
      witness: {
        name: this.props.data.signage.witness.name || '',
        signature: this.props.data.signage.witness.signature || '',
        acceptedTerms: this.props.data.signage.witness.acceptedTerms || false,
        timestampOfAcceptance: this.props.data.signage.witness.timestampOfAcceptance || null,
        datedOn: this.props.data.signage.witness.datedOn ? Services.dates.formatIncomingDate(this.props.data.signage.witness.datedOn) : null
      }
    },
    bankingModal: false,
    eftDetails: {
      default: {
        /*
         * if the account is in Revision, just set some default values so the request goes through, we don't save this data for Revisions,
         * set everything in "default" to match the data format used in every other banking details modal
         */
        accountHolder: this.props.currentAccountMeta.status === 3 ? 'N/A' : '',
        institutionName: this.props.currentAccountMeta.status === 3 ? 'N/A' : '',
        institutionAddress: this.props.currentAccountMeta.status === 3 ? 'N/A' : '',
        institutionNum: this.props.currentAccountMeta.status === 3 ? '123' : '',
        transitNum: this.props.currentAccountMeta.status === 3 ? '12345' : '',
        accountNum: this.props.currentAccountMeta.status === 3 ? '12345678' : '',
        isValidated: false,
        accepted: this.props.currentAccountMeta.status === 3,
        timestampOfAcceptance: this.props.currentAccountMeta.status === 3 ? 1 : null,
        accountHolderError: false,
        institutionNumError: false,
        transitNumError: false,
        accountNumError: false,
        acceptedError: false,
        branchValidationError: false
      }
    },
    modalTypeOpen: false,
    modalTitle: '',
    isEdit: false,
    currentItem: null,
    appSubmitted: false,
    reviewRequestSentSuccessDialogOpen: false,
    lsaModal: false,
    lsaItems: this.props.data.lsaItems || null,
    hasLsa: false,
    errors: {},
    locale: i18n.locale
  };
  _timer = null;
  componentDidMount() {
    const hasLsa = this.props.plan?.spendingAccounts.some(d => d.coverage?.includeLsa === true || d.coverage?.isPooled === true);
    this.setState({
      hasLsa
    });

    // don't auto save when its the authority review screen
    if (this.props.isAuthorityView) {
      return null;
    }
    let initState = this.state;

    // auto save form every 20 seconds
    this._timer = setInterval(() => {
      // don't save if the modal is open
      if (this.state.modalTypeOpen) {
        return;
      }
      if (initState !== this.state) {
        this._save(false, false, false);
        initState = {
          ...this.state
        };
      }
    }, 20000);
    return null;
  }
  componentDidUpdate() {
    this.setState({
      province: localizationMaps.fromEnglish('provinces', this.props.data.province)
    });
  }
  componentWillUnmount() {
    clearInterval(this._timer);
  }
  _validateMasterForm = async () => {
    let isValid = true;
    const fieldsToCheck = ['company', 'numLives', 'addressLineOne', 'province', 'city', 'postalCode', 'nature', 'categories', 'numEmployeesInsured', 'numEmployeesCoveredByWCB'];
    await fieldsToCheck.map(async field => {
      let error = false;

      // We don't need to check certain fields if certain checkmarks aren't checked
      if (field === 'numEmployeesInsured' || field === 'numEmployeesCoveredByWCB') {
        if (!this.state.hasExistingCoverage) {
          // Make sure the fields are set back to a number
          this.setState({
            [field]: 0
          });
        } else {
          // Make sure the value is a number
          error = isNaN(this.state[field]) ? i18n.t(tokens.labels.validations.pleaseEnterValidNumber) : false;
        }
      } else {
        error = await Services.validateInput('checkEmpty', this.state[field]);
      }
      if (error) {
        isValid = false;
        this.setState({
          errors: {
            ...this.state.errors,
            [`${field}Error`]: field === 'categories' ? i18n.t(tokens.labels.validations.pleaseSelectAtLeastOne) : error
          }
        });
      }
      return true;
    });

    // handle stoploss
    if (this.state.stopLoss.status) {
      // Check ot make sure the level has been entered
      const stopLossAmountError = await Services.validateInput('checkEmpty', this.state.stopLoss.amount);
      if (stopLossAmountError) {
        // Let the user know that the stop loss amount is required
        this.setState({
          errors: {
            ...this.state.errors,
            stopLossAmountError
          }
        });
        isValid = false;
      }
    } else {
      // Make sure stoploss amount isn't an empty string
      this.setState({
        stopLoss: {
          ...this.state.stopLoss,
          amount: 0
        }
      });
    }
    if (this.state.hasExistingCoverage && this.state.existingCoverage.length === 0) {
      // Let the user know that the stop loss amount is required
      this.setState({
        errors: {
          ...this.state.errors,
          existingCarrierError: i18n.t(tokens.labels.validations.existingCarrier)
        }
      });
      isValid = false;
    }
    return isValid;
  };
  _validateSignageBlock = async () => {
    const requiresSignageBlock = this.props.isPrivateLabelAdvisor || this.props.currentAccountMeta.state >= 6;
    if (!requiresSignageBlock) {
      return true;
    }
    const isAuthorityView = this.props.isAuthorityView;
    const advisorAcceptedTermsError = isAuthorityView ? null : !this.state.signage.advisor.acceptedTerms;
    const authorityAcceptedTermsError = isAuthorityView ? !this.state.signage.authority.acceptedTerms : null;
    const witnessAcceptedTermsError = isAuthorityView ? !this.state.signage.witness.acceptedTerms : null;
    const acceptedBankingTermsError = isAuthorityView ? !this.state.eftDetails.default.accepted : null;
    let isBankingValid = true;

    // validate the banking details
    if (isAuthorityView && this.props.currentAccountMeta.status !== 3) {
      isBankingValid = this.state.eftDetails.default.accepted;
    }
    if (!isBankingValid) {
      await this.props.displayError({
        verbiage: i18n.t(tokens.labels.validations.bankingDetailsCorrectlyFilledOut)
      });
      return false;
    }
    let advisorSignatureError = isAuthorityView ? null : await Services.validateInput('checkEmpty', this.state.signage.advisor.signature);
    const advisorNameError = isAuthorityView ? null : await Services.validateInput('checkEmpty', this.state.signage.advisor.name);
    const advisorDatedOnError = isAuthorityView ? null : await Services.validateInput('date', this.state.signage.advisor.datedOn, i18n.locale);
    let authoritySignatureError = isAuthorityView ? await Services.validateInput('checkEmpty', this.state.signage.authority.signature) : null;
    const authorityNameError = isAuthorityView ? await Services.validateInput('checkEmpty', this.state.signage.authority.name) : null;
    const authorityDatedOnError = isAuthorityView ? await Services.validateInput('date', this.state.signage.authority.datedOn, i18n.locale) : null;
    let witnessSignatureError = isAuthorityView ? await Services.validateInput('checkEmpty', this.state.signage.witness.signature) : null;
    const witnessNameError = isAuthorityView ? await Services.validateInput('checkEmpty', this.state.signage.witness.name) : null;
    const witnessDatedOnError = isAuthorityView ? await Services.validateInput('date', this.state.signage.witness.datedOn, i18n.locale) : null;
    if (!isAuthorityView && this.state.signage.advisor.signature !== '' && this.state.signage.advisor.name !== '' && this.state.signage.advisor.signature.trim() !== this.state.signage.advisor.name.trim()) {
      advisorSignatureError = i18n.t(tokens.labels.signage.advisorSignageError);
    }
    if (isAuthorityView && this.state.signage.authority.signature !== '' && this.state.signage.authority.name !== '' && this.state.signage.authority.signature.trim() !== this.state.signage.authority.name.trim()) {
      authoritySignatureError = i18n.t(tokens.labels.signage.authoritySignageError);
    }
    if (isAuthorityView && this.state.signage.witness.signature !== '' && this.state.signage.witness.name !== '' && this.state.signage.witness.signature.trim() !== this.state.signage.witness.name.trim()) {
      witnessSignatureError = i18n.t(tokens.labels.signage.advisorSignageError);
    }

    // only need the banking details if it's the authority view and not for Revision
    if (isAuthorityView && this.props.currentAccountMeta.status !== 3) {
      await this.setState({
        errors: {
          ...this.state.errors,
          authoritySignatureError: authoritySignatureError || null,
          authorityNameError: authorityNameError || null,
          authorityDatedOnError: authorityDatedOnError || null,
          witnessSignatureError: witnessSignatureError || null,
          witnessNameError: witnessNameError || null,
          witnessDatedOnError: witnessDatedOnError || null,
          authorityAcceptedTermsError: authorityAcceptedTermsError,
          witnessAcceptedTermsError: witnessAcceptedTermsError,
          acceptedBankingTermsError: acceptedBankingTermsError
        }
      });
    } else {
      await this.setState({
        errors: {
          ...this.state.errors,
          advisorSignatureError: advisorSignatureError || null,
          advisorNameError: advisorNameError || null,
          advisorDatedOnError: advisorDatedOnError || null,
          advisorAcceptedTermsError: advisorAcceptedTermsError
        }
      });
    }
    if (advisorSignatureError || advisorNameError || advisorDatedOnError || advisorAcceptedTermsError || authoritySignatureError || authorityNameError || authorityDatedOnError || authorityAcceptedTermsError || witnessSignatureError || witnessNameError || witnessDatedOnError || witnessAcceptedTermsError || acceptedBankingTermsError) {
      window.scroll(0, 5000);
      return false;
    }
    this.setState({
      errors: {}
    });
    return true;
  };
  _completeMasterApp = async () => {
    const isValid = await this._validateSignageBlock();
    if (!isValid) {
      return false;
    }
    const success = await this.props.completeMasterApp(this.props.currentAccountMeta.accountID);
    if (!success) {
      return false;
    }
    this.setState({
      reviewRequestSentSuccessDialogOpen: true
    });
    return true;
  };
  _onBlur = async type => {
    const newValue = type === 'stopLossAmount' ? this.state.stopLoss.amount : this.state[type];
    this.setState({
      errors: {
        ...this.state.errors,
        [`${type}Error`]: await Services.validateInput('checkEmpty', newValue)
      }
    });
  };
  _onBlurDate = (type, noFuture, isCurrentModal = false) => {
    if (!isCurrentModal) {
      this.setState({
        errors: {
          ...this.state.errors,
          [`${type}Error`]: Services.dates.validateDate(this.state.currentItem[type], noFuture)
        }
      });
    } else {
      this.setState({
        errors: {
          currentModal: {
            ...this.state.errors.currentModal,
            [`${type}Error`]: Services.dates.validateDate(this.state.currentItem[type], noFuture)
          }
        }
      });
    }
  };
  _handleProvinceChange = province => {
    this.setState({
      province
    });
  };
  _handleChange = (prop, subProp) => event => {
    this._checkSignatureBlock();
    if (!subProp) {
      this.setState({
        [prop]: event.target.value
      });
    } else {
      this.setState({
        [prop]: {
          ...this.state[prop],
          [subProp]: event.target.value
        }
      });
    }
  };
  _toggle = (prop, subProp) => {
    this._checkSignatureBlock();
    if (!subProp) {
      this.setState({
        [prop]: !this.state[prop]
      });
    } else {
      this.setState({
        [prop]: {
          ...this.state[prop],
          [subProp]: !this.state[prop][subProp]
        }
      });
    }
  };
  saveLsaItems = items => {
    if (items.length) {
      this.setState({
        lsaItems: [...items]
      });
    }
    return true;
  };
  _removeLsaItem = item => {
    this.setState({
      lsaItems: [...this.state.lsaItems.filter(x => x !== item)]
    });
  };
  _toggleModal = state => {
    this.setState({
      modalTypeOpen: state
    });
  };
  _handleCategoryChange = category => {
    this._checkSignatureBlock();
    const index = this.state.categories.indexOf(category);
    if (index === -1) {
      this.setState({
        categories: [category, ...this.state.categories],
        errors: {
          ...this.state.errors,
          categoriesError: false
        }
      });
    } else {
      this.setState({
        categories: this.state.categories.slice(0, index).concat(this.state.categories.slice(index + 1, this.state.categories.length))
      });
    }
  };

  /**
   * Open the modal and set the current currentItem based on the type of modal we are opening
   * If its an edit, we need the uuid so we know which item we are changing
   * @param type
   * @param isEdit
   * @param uuid
   * @private
   */
  _openModal = async (type, isEdit = false, uuid = false) => {
    if (this.props.isAuthorityView || this.props.isReadOnly) {
      return null;
    }
    this._save();
    let currentItem = {};
    let modalTitle = '';
    if (type === 'dependentsWithSurvivorBenefits') {
      modalTitle = i18n.t(tokens.labels.masterApplication.dependentSurvivorBenefit);
      currentItem = {
        name: '',
        expiryDate: ''
      };
    } else if (type === 'employeesNotActivelyAtWork') {
      modalTitle = i18n.t(tokens.labels.masterApplication.employeeNoWork);
      currentItem = {
        name: '',
        dateLastAtWork: '',
        expectedReturnDate: '',
        reasonNotAtWork: '',
        claimStatus: ''
      };
    } else if (type === 'contractEmployees') {
      modalTitle = i18n.t(tokens.labels.masterApplication.contractEmployeeSingle);
      currentItem = {
        name: '',
        occupation: '',
        contractStartDate: '',
        contractEndDate: ''
      };
    } else if (type === 'exclusionsFromCoverage') {
      modalTitle = i18n.t(tokens.labels.masterApplication.excludedEmployee);
      currentItem = {
        name: '',
        reasonForExclusion: ''
      };
    } else if (type === 'existingCoverage') {
      modalTitle = i18n.t(tokens.labels.masterApplication.existingCarrier);
      currentItem = {
        carrier: '',
        benefits: []
      };
    }

    // if its an edit, find the item using the provided uuid
    if (isEdit && uuid) {
      modalTitle = `Edit ${modalTitle}`;
      [currentItem] = await this.state[type].filter(record => record.uuid === uuid);
    }
    this.setState({
      modalTypeOpen: type,
      isEdit,
      currentItem,
      modalTitle
    });
    return null;
  };
  _closeModal = () => {
    this.setState({
      modalTypeOpen: false,
      isEdit: false,
      errors: {
        ...this.state.errors,
        currentModal: {}
      }
    });
  };

  // on any change, if the signage block has been set, unset it and make them re-sign the doc
  _checkSignatureBlock = () => {
    const requiresSignageBlock = this.props.currentAccountMeta.state >= 6;
    if (!requiresSignageBlock) {
      return null;
    }
    if (this.props.isAuthorityView) {
      return null;
    }
    if (this.state.signage.advisor.signature !== '' || this.state.signage.advisor.acceptedTerms) {
      this.setState({
        signage: {
          ...this.state.signage,
          advisor: {
            ...this.state.signage.advisor,
            signature: '',
            acceptedTerms: false,
            datedOn: null
          }
        }
      });
    }
    return null;
  };
  _closeReviewRequestSentSuccessDialog = () => {
    this.setState({
      reviewRequestSentSuccessDialogOpen: false
    });
    this._goToNextStep();
  };

  /**
   * Set next step to false so we go back to the Index
   */
  _goToNextStep = () => {
    this.props.setSelectedSection(false);
  };
  _modalFormItemBlur = async type => {
    this.setState({
      errors: {
        currentModal: {
          ...this.state.errors.currentModal,
          [`${type}Error`]: await Services.validateInput("checkEmpty", this.state.currentItem[type])
        }
      }
    });
  };
  _handleModalFormItemChange = (field, subField) => e => {
    this._checkSignatureBlock();
    if (this.state.modalTypeOpen === 'existingCoverage' && field === 'benefits') {
      const index = this.state.currentItem.benefits.indexOf(subField);
      const items = this.state.currentItem.benefits;
      this.setState({
        currentItem: {
          ...this.state.currentItem,
          benefits: index === -1 ? [subField, ...this.state.currentItem.benefits] : items.slice(0, index).concat(items.slice(index + 1, items.length))
        },
        errors: {
          currentModal: {
            ...this.state.errors.currentModal,
            benefitsError: false
          }
        }
      });
    } else {
      this.setState({
        currentItem: {
          ...this.state.currentItem,
          [field]: e.target.value
        }
      });
    }
  };
  _handleSignageBlockChange = (prop, subProp, value) => {
    const requiresSignageBlock = this.props.isPrivateLabelAdvisor || this.props.currentAccountMeta.state >= 6;
    if (!requiresSignageBlock) {
      return null;
    }
    if (subProp === 'acceptedTerms' && value === true) {
      this.setState({
        signage: {
          ...this.state.signage,
          [prop]: {
            ...this.state.signage[prop],
            acceptedTerms: true,
            timestampOfAcceptance: dates.getUTC()
          }
        }
      });
      return null;
    }
    this.setState({
      signage: {
        ...this.state.signage,
        [prop]: {
          ...this.state.signage[prop],
          [subProp]: value
        }
      }
    });
    return null;
  };
  _addArrayItem = async () => {
    const type = this.state.modalTypeOpen;
    const item = this.state.currentItem;
    const itemKeys = Object.keys(item);
    let isInvalid = false;

    // Validate the form
    await itemKeys.map(async key => {
      let error = '';
      switch (key) {
        case 'expiryDate' || 'expectedReturnDate' || 'contractEndDate':
          error = Services.dates.validateDate(item[key], false);
          break;
        case 'dateLastAtWork' || 'contractStartDate':
          error = Services.dates.validateDate(item[key]);
          break;
        default:
          error = await Services.validateInput("checkEmpty", item[key]);
          break;
      }
      if (error) {
        isInvalid = true;

        // Set error for input too
        this.setState({
          errors: {
            currentModal: {
              ...this.state.errors.currentModal,
              [`${key}Error`]: key === 'benefits' ? 'Please select at least one' : error
            }
          }
        });
      }
      return true;
    });
    if (!isInvalid) {
      // add a unique identifier just for removing and editing
      item.uuid = new Date().getTime();
      this.setState({
        [type]: [item, ...this.state[type]]
      });
      this._save();
      this._closeModal();
    }
  };
  _updateArrayItem = async () => {
    const type = this.state.modalTypeOpen;
    const item = this.state.currentItem;
    const itemKeys = Object.keys(item);
    let isInvalid = false;

    // Validate the form
    await itemKeys.map(async key => {
      switch (key) {
        case 'expiryDate':
          isInvalid = Services.dates.validateDate(item[key], false) ? true : isInvalid;
          break;
        default:
          isInvalid = (await Services.validateInput("checkEmpty", item[key])) ? true : isInvalid;
          break;
      }
      return true;
    });
    if (!isInvalid) {
      this.setState({
        [type]: this.state[type].map(record => {
          if (record.uuid !== item.uuid) {
            return record;
          }
          return item;
        })
      });
      this._save();
      this._closeModal();
    }
  };
  _removeArrayItem = (type, uuid) => {
    this.setState({
      [type]: this.state[type].filter(record => record.uuid !== uuid)
    });
  };
  _save = async (showLoader = false, showSuccess = true, isSubmit = false) => {
    if (this.props.isReadOnly) {
      return false;
    }
    let isSigningValid = true;

    // Only validate signage on submit
    if (isSubmit) {
      isSigningValid = await this._validateSignageBlock(isSubmit);
    }
    if (!isSigningValid) {
      window.scroll(0, 2500);
      return false;
    }
    const isFormValid = await this._validateMasterForm();
    if (!isFormValid) {
      if (showLoader) {
        window.scroll(0, 0);
      }
      return false;
    }
    const data = {
      ...this.state,
      province: localizationMaps.toEnglish('provinces', this.state.province),
      signage: {
        ...this.state.signage,
        advisor: {
          ...this.state.signage.advisor,
          datedOn: this.state.signage.advisor.datedOn ? Services.dates.formatOutgoingDate(this.state.signage.advisor.datedOn) : null
        },
        authority: {
          ...this.state.signage.authority,
          datedOn: this.state.signage.authority.datedOn ? Services.dates.formatOutgoingDate(this.state.signage.authority.datedOn) : null
        },
        witness: {
          ...this.state.signage.witness,
          datedOn: this.state.signage.witness.datedOn ? Services.dates.formatOutgoingDate(this.state.signage.witness.datedOn) : null
        }
      },
      dependentsWithSurvivorBenefits: this.state.dependentsWithSurvivorBenefits.map(dependent => ({
        ...dependent,
        expiryDate: Services.dates.formatOutgoingDate(dependent.expiryDate)
      })),
      employeesNotActivelyAtWork: this.state.employeesNotActivelyAtWork.map(employee => ({
        ...employee,
        dateLastAtWork: Services.dates.formatOutgoingDate(employee.dateLastAtWork),
        expectedReturnDate: Services.dates.formatOutgoingDate(employee.expectedReturnDate)
      })),
      contractEmployees: this.state.contractEmployees.map(employee => ({
        ...employee,
        contractEndDate: Services.dates.formatOutgoingDate(employee.contractEndDate),
        contractStartDate: Services.dates.formatOutgoingDate(employee.contractStartDate)
      }))
    };
    delete data.modalTypeOpen;
    delete data.modalTitle;
    delete data.isEdit;
    delete data.currentItem;
    delete data.appSubmitted;
    delete data.reviewRequestSentSuccessDialogOpen;
    delete data.errors;
    delete data.lsaModal;
    delete data.hasLsa;

    // remove the blank banking fields
    delete data.eftDetails;
    delete data.bankingModal;

    // remove the form language
    delete data.locale;
    if (showLoader) {
      window.location.href = '#top';
    }
    const success = await this.props.updateMasterApp(this.props.currentAccountMeta.accountID, data, showLoader, showSuccess);
    if (success) {
      return true;
    }
    return false;
  };
  _saveAndSubmit = async () => {
    // Validate First
    const isFormValid = await this._save(true, false, true);
    if (isFormValid) {
      return this._completeMasterApp();
    }
    return false;
  };
  _sendForApproval = async () => {
    const isValid = await this._validateSignageBlock();
    if (!isValid) {
      return false;
    }
    const success = await this.props.sendRequestApprovalLink(this.props.currentAccountMeta.accountID);
    if (success) {
      this.setState({
        reviewRequestSentSuccessDialogOpen: true
      });
      return true;
    }
    return false;
  };
  _saveAndSendForApproval = async () => {
    // Validate First
    const isFormValid = await this._save(true, false, true);
    if (isFormValid) {
      return this._sendForApproval();
    }
    return false;
  };
  _submitAuthorityView = async () => {
    const isValid = await this._validateSignageBlock();
    if (!isValid) {
      return false;
    }
    const data = {
      ...this.state,
      province: localizationMaps.toEnglish('provinces', this.state.province),
      signage: {
        ...this.state.signage,
        advisor: {
          ...this.state.signage.advisor,
          datedOn: this.state.signage.advisor.datedOn ? Services.dates.formatOutgoingDate(this.state.signage.advisor.datedOn) : null
        },
        authority: {
          ...this.state.signage.authority,
          datedOn: this.state.signage.authority.datedOn ? Services.dates.formatOutgoingDate(this.state.signage.authority.datedOn) : null
        },
        witness: {
          ...this.state.signage.witness,
          datedOn: this.state.signage.witness.datedOn ? Services.dates.formatOutgoingDate(this.state.signage.witness.datedOn) : null
        }
      },
      bankingAccountHolder: this.state.eftDetails.default.accountHolder,
      bankingInstitutionName: this.state.eftDetails.default.institutionName,
      bankingInstitutionAddress: this.state.eftDetails.default.institutionAddress,
      bankingInstitutionNum: this.state.eftDetails.default.institutionNum,
      bankingTransitNum: this.state.eftDetails.default.transitNum,
      bankingAccountNum: this.state.eftDetails.default.accountNum,
      bankingAccepted: this.state.eftDetails.default.accepted,
      bankingTimestampOfAcceptance: this.state.eftDetails.default.timestampOfAcceptance
    };
    delete data.modalTypeOpen;
    delete data.modalTitle;
    delete data.isEdit;
    delete data.currentItem;
    delete data.appSubmitted;
    delete data.reviewRequestSentSuccessDialogOpen;
    delete data.errors;
    delete data.lsaModal;
    delete data.hasLsa;

    // remove the extra banking fields
    delete data.bankingDetails;
    delete data.bankingModal;
    delete data.eftDetails;
    const success = await this.props.submitMasterApp(this.props.currentAccountMeta.accountID, data);
    if (success) {
      this.setState({
        appSubmitted: true
      });
      return true;
    }
    return false;
  };
  _saveNewBankingDetails = newBankingDetails => {
    this.setState({
      ...this.state,
      eftDetails: {
        ...newBankingDetails
      }
    });
  };
  _handleEditBankingDetails = () => {
    this.setState({
      ...this.state,
      eftDetails: {
        default: {
          ...this.state.eftDetails.default,
          isValidated: false
        }
      }
    });
    this._toggle("bankingModal");
  };

  /**
   * 
   * @param {*} input 
   */
  _formatPostalCode = async input => {
    const formattedPostalCode = input.toUpperCase().trim().replace(/\s+/g, '') // Remove all spaces
    .replace(/([A-Z0-9]{3})([A-Z0-9]{3})/, '$1 $2'); // Add a space after the first 3 characters

    await this.setState({
      postalCode: formattedPostalCode
    });
    this._onBlur('postalCode');
  };
  render() {
    const {
      isReadOnly
    } = this.props;
    let isAuthorityView = Boolean(this.props.isAuthorityView);
    const styles = {
      saveAndSendBtn: {
        marginRight: 1
      },
      signageAcceptBox: {
        paddingTop: '0',
        marginTop: '-15px',
        paddingRight: "8px",
        paddingLeft: "8px",
        paddingBottom: "8px"
      },
      signature: {
        fontFamily: 'AntaraDistance',
        fontWeight: 'bold'
      },
      row: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-around',
        width: '100%'
      },
      column: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-around',
        marginTop: 20
      },
      goBack: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: 'rgb(247, 247, 247)'
      },
      spacer: {
        // 15 pixels for Eric
        height: 15
      },
      chip: {
        margin: 3
      },
      formLabel: {
        fontFamily: Theme.fonts.type.light
      },
      gridStyle: {
        padding: "8px",
        flex: 1
      },
      signStyle: {
        "& .MuiInput-underline:hover:not(.Mui-disabled):not(.Mui-error):before": {
          borderBottomWidth: "2px"
        }
      },
      errorStyle: {
        "&. MuiFormHelperText-root.Mui-error": {
          marginLeft: "0px"
        }
      },
      divider: {
        width: 40
      }
    };
    if (isReadOnly) {
      isAuthorityView = true;
    }
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
      id: "top"
    }), this.state.appSubmitted || isReadOnly ? null : isAuthorityView ? /*#__PURE__*/React.createElement(MessageCard, {
      title: i18n.t(tokens.labels.masterApplication.reviewAndSignApplication),
      subtitle: i18n.t(tokens.labels.masterApplication.reviewMasterApplication),
      btn: true,
      btnName: i18n.t(tokens.buttons.submit),
      btnOnClick: this._submitAuthorityView
    }) : this.props.sendRequestApprovalLink ? /*#__PURE__*/React.createElement(MessageCard, {
      title: i18n.t(tokens.labels.masterApplication.sendForApproval),
      subtitle: i18n.t(tokens.labels.masterApplication.reviewFinalizedMasterApplication),
      btn: true,
      btnName: i18n.t(tokens.buttons.sendForApproval),
      btnOnClick: this._saveAndSendForApproval,
      btnsAbove: true
    }) : /*#__PURE__*/React.createElement(MessageCard, {
      title: i18n.t(tokens.labels.masterApplication.completeAndSubmit),
      subtitle: i18n.t(tokens.labels.masterApplication.completeTheMasterApplication),
      btn: this.props.currentAccountMeta.state < 3,
      btnName: i18n.t(tokens.buttons.submit),
      btnOnClick: this._saveAndSubmit,
      btnsAbove: true
    }), /*#__PURE__*/React.createElement("div", {
      style: {
        backgroundColor: colors.surface,
        boxShadow: '0 1px 2px rgba(0, 0, 0, .15)',
        display: 'block',
        margin: 'auto auto 30px auto',
        padding: '22px',
        position: 'relative',
        zIndex: 3
      }
    }, /*#__PURE__*/React.createElement(Text, {
      mediumLarge: true,
      bold: true,
      display: "block",
      align: "left",
      margin: "20px 8px 20px 8px",
      customLinkStyle: {
        lineHeight: '40px'
      }
    }, i18n.t(tokens.labels.masterApplication.groupApplicantsData)), /*#__PURE__*/React.createElement(Grid, {
      container: true,
      direction: "row",
      alignItems: "center"
    }, /*#__PURE__*/React.createElement(Grid, {
      item: true,
      sx: styles.gridStyle,
      xs: 12,
      sm: 6
    }, /*#__PURE__*/React.createElement(TextInput, {
      label: i18n.t(tokens.labels.account.companyName),
      value: this.state.company,
      onBlur: () => this._onBlur('company'),
      onChange: this._handleChange('company'),
      error: Boolean(this.state.errors.companyError),
      disabled: isAuthorityView,
      fullWidth: true,
      required: true
    })), /*#__PURE__*/React.createElement(Grid, {
      item: true,
      sx: styles.gridStyle,
      xs: 12,
      sm: 6
    }, /*#__PURE__*/React.createElement(NumberInput, {
      label: i18n.t(tokens.labels.masterApplication.totalLivesToBeInsured),
      fullWidth: true,
      required: true,
      value: this.state.numLives,
      onBlur: () => this._onBlur('numLives'),
      onChange: this._handleChange('numLives'),
      error: Boolean(this.state.errors.numLivesError),
      disabled: isAuthorityView
    })), /*#__PURE__*/React.createElement(Grid, {
      item: true,
      sx: styles.gridStyle,
      xs: 12,
      style: styles.row
    }, /*#__PURE__*/React.createElement(TextInput, {
      label: i18n.t(tokens.labels.addressLineOne),
      fullWidth: true,
      required: true,
      value: this.state.addressLineOne,
      onBlur: () => this._onBlur('addressLineOne'),
      onChange: this._handleChange('addressLineOne'),
      error: Boolean(this.state.errors.addressLineOneError),
      helperText: this.state.errors.addressLineOneError,
      disabled: isAuthorityView
    }), /*#__PURE__*/React.createElement("div", {
      style: styles.divider
    }), /*#__PURE__*/React.createElement(TextInput, {
      label: i18n.t(tokens.labels.addressLineTwo),
      fullWidth: true,
      value: this.state.addressLineTwo,
      onBlur: () => this._onBlur('addressLineTwo'),
      onChange: this._handleChange('addressLineTwo'),
      helperText: !isAuthorityView ? i18n.t(tokens.labels.addressLineTwoExample) : null,
      disabled: isAuthorityView
    })), /*#__PURE__*/React.createElement(Grid, {
      item: true,
      sx: styles.gridStyle,
      xs: 12,
      style: styles.row
    }, /*#__PURE__*/React.createElement(TextInput, {
      label: i18n.t(tokens.labels.city),
      fullWidth: true,
      required: true,
      value: this.state.city,
      onBlur: () => this._onBlur('city'),
      onChange: this._handleChange('city'),
      helperText: this.state.errors.cityError,
      disabled: isAuthorityView
    }), /*#__PURE__*/React.createElement("div", {
      style: styles.divider
    }), /*#__PURE__*/React.createElement(TextInput, {
      label: i18n.t(tokens.labels.province),
      autocomplete: "shipping region",
      value: this.state.province,
      onBlur: () => this._onBlur('province'),
      onChange: e => this._handleProvinceChange(e.target.value),
      select: true,
      selectOptions: Services.selectFieldOptions.provinceOptions(),
      fullWidth: true,
      required: true,
      disabled: isAuthorityView
    })), /*#__PURE__*/React.createElement(Grid, {
      item: true,
      sx: styles.gridStyle,
      xs: 12,
      style: styles.row
    }, /*#__PURE__*/React.createElement(TextInput, {
      label: i18n.t(tokens.labels.postalCode),
      fullWidth: true,
      required: true,
      value: this.state.postalCode,
      onBlur: e => this._formatPostalCode(e.target.value),
      onChange: e => {
        // Allow typing only 6 alphanumeric characters and trim any excess
        const postalCodeValue = e.target.value.toUpperCase().slice(0, 7);
        this.setState({
          postalCode: postalCodeValue
        });
      },
      disabled: isAuthorityView
    }), /*#__PURE__*/React.createElement("div", {
      style: styles.divider
    }), /*#__PURE__*/React.createElement("div", {
      style: {
        width: '100%'
      }
    })), /*#__PURE__*/React.createElement(Grid, {
      item: true,
      sx: styles.gridStyle,
      xs: 12
    }, /*#__PURE__*/React.createElement(TextInput, {
      label: i18n.t(tokens.labels.masterApplication.natureOfBusiness),
      fullWidth: true,
      required: true,
      value: this.state.nature,
      onBlur: () => this._onBlur('nature'),
      onChange: this._handleChange('nature'),
      error: Boolean(this.state.errors.natureError),
      helperText: this.state.errors.natureError,
      disabled: isAuthorityView
    })), /*#__PURE__*/React.createElement(Grid, {
      item: true,
      sx: styles.gridStyle,
      xs: 12,
      sm: 7
    }, /*#__PURE__*/React.createElement(FormControl, {
      component: "fieldset",
      error: Boolean(this.state.errors.categoriesError)
    }, /*#__PURE__*/React.createElement(FormLabel, {
      sx: styles.formLabel,
      component: "legend",
      required: true
    }, i18n.t(tokens.labels.masterApplication.businessIs)), /*#__PURE__*/React.createElement(FormGroup, {
      row: true
    }, /*#__PURE__*/React.createElement(FormControlLabel, {
      disabled: isAuthorityView,
      control: /*#__PURE__*/React.createElement(Checkbox, {
        checked: this.state.categories.indexOf('corporation') > -1,
        onChange: () => this._handleCategoryChange('corporation'),
        value: "corporation",
        disabled: isAuthorityView,
        color: "secondary"
      }),
      label: /*#__PURE__*/React.createElement(Text, {
        customStyle: isAuthorityView ? {
          color: `rgba(0, 0, 0, 0.38)`
        } : {},
        avMedium: true
      }, i18n.t(tokens.labels.masterApplication.corporation))
    }), /*#__PURE__*/React.createElement(FormControlLabel, {
      disabled: isAuthorityView,
      control: /*#__PURE__*/React.createElement(Checkbox, {
        checked: this.state.categories.indexOf('partnership') > -1,
        onChange: () => this._handleCategoryChange('partnership'),
        value: "partnership",
        disabled: isAuthorityView,
        color: "secondary"
      }),
      label: /*#__PURE__*/React.createElement(Text, {
        customStyle: isAuthorityView ? {
          color: `rgba(0, 0, 0, 0.38)`
        } : {},
        avMedium: true
      }, i18n.t(tokens.labels.masterApplication.partnership))
    }), /*#__PURE__*/React.createElement(FormControlLabel, {
      disabled: isAuthorityView,
      control: /*#__PURE__*/React.createElement(Checkbox, {
        checked: this.state.categories.indexOf('sole proprietorship') > -1,
        onChange: () => this._handleCategoryChange('sole proprietorship'),
        value: "sole proprietorship",
        disabled: isAuthorityView,
        color: "secondary"
      }),
      label: /*#__PURE__*/React.createElement(Text, {
        customStyle: isAuthorityView ? {
          color: `rgba(0, 0, 0, 0.38)`
        } : {},
        avMedium: true
      }, i18n.t(tokens.labels.masterApplication.soleProprietorship))
    }), /*#__PURE__*/React.createElement(FormControlLabel, {
      disabled: isAuthorityView,
      control: /*#__PURE__*/React.createElement(Checkbox, {
        checked: this.state.categories.indexOf('unionized') > -1,
        onChange: () => this._handleCategoryChange('unionized'),
        value: "unionized",
        disabled: isAuthorityView,
        color: "secondary"
      }),
      label: /*#__PURE__*/React.createElement(Text, {
        customStyle: isAuthorityView ? {
          color: `rgba(0, 0, 0, 0.38)`
        } : {},
        avMedium: true
      }, i18n.t(tokens.labels.masterApplication.unionized))
    }), /*#__PURE__*/React.createElement(FormControlLabel, {
      disabled: isAuthorityView,
      control: /*#__PURE__*/React.createElement(Checkbox, {
        checked: this.state.categories.indexOf('joint venture') > -1,
        onChange: () => this._handleCategoryChange('joint venture'),
        value: "joint venture",
        disabled: isAuthorityView,
        color: "secondary"
      }),
      label: /*#__PURE__*/React.createElement(Text, {
        customStyle: isAuthorityView ? {
          color: `rgba(0, 0, 0, 0.38)`
        } : {},
        avMedium: true
      }, i18n.t(tokens.labels.masterApplication.jointVenture))
    })), this.state.errors.categoriesError && /*#__PURE__*/React.createElement(Text, {
      small: true,
      color: "#d93025",
      display: "block",
      align: "left"
    }, this.state.errors.categoriesError))), /*#__PURE__*/React.createElement(Grid, {
      item: true,
      sx: styles.gridStyle,
      xs: 12,
      sm: this.state.hasExistingCoverage ? 4 : 12
    }, /*#__PURE__*/React.createElement(FormLabel, {
      sx: styles.formLabel
    }, i18n.t(tokens.labels.masterApplication.existingGroupBenefits)), /*#__PURE__*/React.createElement(FormGroup, {
      row: true
    }, /*#__PURE__*/React.createElement(FormControlLabel, {
      disabled: isAuthorityView,
      control: /*#__PURE__*/React.createElement(Checkbox, {
        checked: this.state.hasExistingCoverage,
        onChange: () => this._toggle('hasExistingCoverage'),
        value: "hasExistingCoverage",
        disabled: isAuthorityView,
        color: "secondary"
      }),
      label: /*#__PURE__*/React.createElement(Text, {
        customStyle: isAuthorityView ? {
          color: `rgba(0, 0, 0, 0.38)`
        } : {},
        avMedium: true
      }, i18n.t(tokens.labels.masterApplication.applicantHasExistingGroupBenefits))
    }))), this.state.hasExistingCoverage && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Grid, {
      item: true,
      sx: styles.gridStyle,
      xs: 12,
      sm: 4
    }, /*#__PURE__*/React.createElement(NumberInput, {
      label: i18n.t(tokens.labels.masterApplication.employeesCurrentlyInsured),
      fullWidth: true,
      required: true,
      value: this.state.numEmployeesInsured,
      onBlur: () => this._onBlur('numEmployeesInsured'),
      onChange: this._handleChange('numEmployeesInsured'),
      error: Boolean(this.state.errors.numEmployeesInsuredError),
      disabled: isAuthorityView
    })), /*#__PURE__*/React.createElement(Grid, {
      item: true,
      sx: styles.gridStyle,
      xs: 12,
      sm: 4
    }, /*#__PURE__*/React.createElement(NumberInput, {
      label: i18n.t(tokens.labels.masterApplication.employeesCoveredByWCBWSIB),
      fullWidth: true,
      required: true,
      value: this.state.numEmployeesCoveredByWCB,
      onBlur: () => this._onBlur('numEmployeesCoveredByWCB'),
      onChange: this._handleChange('numEmployeesCoveredByWCB'),
      error: Boolean(this.state.errors.numEmployeesCoveredByWCBError),
      disabled: isAuthorityView
    })), /*#__PURE__*/React.createElement(Grid, {
      item: true,
      sx: styles.gridStyle,
      xs: 12
    }, /*#__PURE__*/React.createElement(FormGroup, {
      row: true,
      onClick: () => this._openModal('existingCoverage')
    }, /*#__PURE__*/React.createElement(FormLabel, {
      sx: styles.formLabel,
      error: Boolean(this.state.errors.existingCarrierError),
      required: true
    }, i18n.t(tokens.labels.masterApplication.existingCarrier)), !isAuthorityView && /*#__PURE__*/React.createElement(AddIcon, {
      style: {
        fontSize: 19,
        marginTop: 2.5
      }
    })), /*#__PURE__*/React.createElement("div", {
      style: {
        marginTop: 10
      }
    }, this.state.existingCoverage.length === 0 ? /*#__PURE__*/React.createElement(Text, null, i18n.t(tokens.labels.masterApplication.none)) : this.state.existingCoverage.map(item => /*#__PURE__*/React.createElement(ExistingCoverageBlock, {
      key: item.uuid,
      data: item,
      remove: () => this._removeArrayItem('existingCoverage', item.uuid),
      edit: () => this._openModal('existingCoverage', true, item.uuid),
      isAuthorityView: isAuthorityView
    }))))), /*#__PURE__*/React.createElement(Grid, {
      item: true,
      sx: styles.gridStyle,
      xs: 12
    }, /*#__PURE__*/React.createElement(FormGroup, {
      row: true,
      onClick: () => this._openModal('dependentsWithSurvivorBenefits')
    }, /*#__PURE__*/React.createElement(FormLabel, {
      sx: styles.formLabel
    }, i18n.t(tokens.labels.masterApplication.dependentsWithSurvivorBenefits)), !isAuthorityView && /*#__PURE__*/React.createElement(AddIcon, {
      style: {
        fontSize: 19,
        marginTop: 2.5
      }
    })), /*#__PURE__*/React.createElement("div", {
      style: {
        marginTop: 10
      }
    }, this.state.dependentsWithSurvivorBenefits.length === 0 ? /*#__PURE__*/React.createElement(Text, null, i18n.t(tokens.labels.masterApplication.none)) : this.state.dependentsWithSurvivorBenefits.map(item => /*#__PURE__*/React.createElement(SurvivorBenefitDependentRow, {
      key: item.uuid,
      data: item,
      remove: () => this._removeArrayItem('dependentsWithSurvivorBenefits', item.uuid),
      edit: () => this._openModal('dependentsWithSurvivorBenefits', true, item.uuid),
      isAuthorityView: isAuthorityView
    })))), /*#__PURE__*/React.createElement(Grid, {
      item: true,
      sx: styles.gridStyle,
      xs: 12
    }, /*#__PURE__*/React.createElement(FormGroup, {
      row: true,
      onClick: () => this._openModal('employeesNotActivelyAtWork')
    }, /*#__PURE__*/React.createElement(FormLabel, {
      sx: styles.formLabel
    }, i18n.t(tokens.labels.masterApplication.employeesNotAtWork)), !isAuthorityView && /*#__PURE__*/React.createElement(AddIcon, {
      style: {
        fontSize: 19,
        marginTop: 2.5
      }
    })), /*#__PURE__*/React.createElement("div", {
      style: {
        marginTop: 10
      }
    }, this.state.employeesNotActivelyAtWork.length === 0 ? /*#__PURE__*/React.createElement(Text, null, i18n.t(tokens.labels.masterApplication.none)) : this.state.employeesNotActivelyAtWork.map(item => /*#__PURE__*/React.createElement(EmployeeNotAtWorkRow, {
      key: item.uuid,
      data: item,
      remove: () => this._removeArrayItem('employeesNotActivelyAtWork', item.uuid),
      edit: () => this._openModal('employeesNotActivelyAtWork', true, item.uuid),
      isAuthorityView: isAuthorityView
    })))), /*#__PURE__*/React.createElement(Grid, {
      item: true,
      sx: styles.gridStyle,
      xs: 12
    }, /*#__PURE__*/React.createElement(FormGroup, {
      row: true,
      onClick: () => this._openModal('contractEmployees')
    }, /*#__PURE__*/React.createElement(FormLabel, {
      sx: styles.formLabel
    }, i18n.t(tokens.labels.masterApplication.contractEmployees)), !isAuthorityView && /*#__PURE__*/React.createElement(AddIcon, {
      style: {
        fontSize: 19,
        marginTop: 2.5
      }
    })), /*#__PURE__*/React.createElement("div", {
      style: {
        marginTop: 10
      }
    }, this.state.contractEmployees.length === 0 ? /*#__PURE__*/React.createElement(Text, null, i18n.t(tokens.labels.masterApplication.none)) : this.state.contractEmployees.map(item => /*#__PURE__*/React.createElement(ContractEmployeeRow, {
      key: item.uuid,
      data: item,
      remove: () => this._removeArrayItem('contractEmployees', item.uuid),
      edit: () => this._openModal('contractEmployees', true, item.uuid),
      isAuthorityView: isAuthorityView
    })))), /*#__PURE__*/React.createElement(Grid, {
      item: true,
      sx: styles.gridStyle,
      xs: 12
    }, /*#__PURE__*/React.createElement(FormGroup, {
      row: true,
      onClick: () => this._openModal('exclusionsFromCoverage')
    }, /*#__PURE__*/React.createElement(FormLabel, {
      sx: styles.formLabel
    }, i18n.t(tokens.labels.masterApplication.excludedFromGroupBenefits)), !isAuthorityView && /*#__PURE__*/React.createElement(AddIcon, {
      style: {
        fontSize: 19,
        marginTop: 2.5
      }
    })), /*#__PURE__*/React.createElement("div", {
      style: {
        marginTop: 10
      }
    }, this.state.exclusionsFromCoverage.length === 0 ? /*#__PURE__*/React.createElement(Text, null, i18n.t(tokens.labels.masterApplication.none)) : this.state.exclusionsFromCoverage.map(item => /*#__PURE__*/React.createElement(ExcludedFromBenefitsRow, {
      key: item.uuid,
      data: item,
      remove: () => this._removeArrayItem('exclusionsFromCoverage', item.uuid),
      edit: () => this._openModal('exclusionsFromCoverage', true, item.uuid),
      isAuthorityView: isAuthorityView
    })))), !this.props.currentAccountMeta.privateLabelID && /*#__PURE__*/React.createElement(Grid, {
      item: true,
      sx: styles.gridStyle,
      xs: 12,
      sm: this.state.stopLoss.status ? 3 : 12
    }, /*#__PURE__*/React.createElement(FormLabel, {
      sx: styles.formLabel
    }, i18n.t(tokens.labels.masterApplication.stopLoss)), /*#__PURE__*/React.createElement(FormGroup, {
      row: true
    }, /*#__PURE__*/React.createElement(FormControlLabel, {
      disabled: isAuthorityView,
      control: /*#__PURE__*/React.createElement(Checkbox, {
        checked: this.state.stopLoss.status,
        onChange: () => this._toggle('stopLoss', 'status'),
        disabled: isAuthorityView,
        value: "hasStopLoss",
        color: "secondary"
      }),
      label: /*#__PURE__*/React.createElement(Text, {
        customStyle: isAuthorityView ? {
          color: `rgba(0, 0, 0, 0.38)`
        } : {},
        avMedium: true
      }, i18n.t(tokens.labels.masterApplication.applicantHasStopLoss))
    }))), this.state.stopLoss.status && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Grid, {
      item: true,
      sx: styles.gridStyle,
      xs: 12,
      sm: 4
    }, /*#__PURE__*/React.createElement(NumberInput, {
      label: i18n.t(tokens.labels.masterApplication.stopLossLevel),
      fullWidth: true,
      required: true,
      value: this.state.stopLoss.amount,
      onBlur: () => this._onBlur('stopLossAmount'),
      onChange: this._handleChange('stopLoss', 'amount'),
      error: Boolean(this.state.errors.stopLossAmountError),
      prefix: true,
      disabled: isAuthorityView
    })), /*#__PURE__*/React.createElement(Grid, {
      item: true,
      sx: styles.gridStyle,
      xs: 12,
      sm: 5
    }, /*#__PURE__*/React.createElement(FormControlLabel, {
      disabled: isAuthorityView,
      control: /*#__PURE__*/React.createElement(Checkbox, {
        checked: this.state.stopLoss.insuredsWithRecurringClaims,
        onChange: () => this._toggle('stopLoss', 'insuredsWithRecurringClaims'),
        value: "insuredsWithRecurringClaims",
        disabled: isAuthorityView,
        color: "secondary"
      }),
      label: /*#__PURE__*/React.createElement(Text, {
        customStyle: isAuthorityView ? {
          color: `rgba(0, 0, 0, 0.38)`
        } : {},
        avMedium: true
      }, i18n.t(tokens.labels.masterApplication.recurringEHCClaims))
    }))), isAuthorityView && this.state.hasLsa && !isReadOnly && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Grid, {
      item: true,
      sx: styles.gridStyle,
      xs: 12
    }, /*#__PURE__*/React.createElement(Text, null, i18n.t(tokens.labels.spendingAccounts.lifestyleSpendingAccount)), /*#__PURE__*/React.createElement(Text, {
      small: true
    }, i18n.t(tokens.labels.masterApplication.lifestyleSpendingAccount)), /*#__PURE__*/React.createElement(Button, {
      marginTop: "20px",
      onClick: () => this._toggle("lsaModal"),
      color: "primary"
    }, i18n.t(tokens.buttons.selectItems)))), this.state.hasLsa && this.state.lsaItems && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Grid, {
      item: true,
      sx: styles.gridStyle,
      xs: 12
    }, /*#__PURE__*/React.createElement(Text, {
      small: true
    }, i18n.t(tokens.labels.masterApplication.selectedLifestyleItems)), this.state.lsaItems.map(item => /*#__PURE__*/React.createElement(Chip, {
      key: item,
      label: localizationMaps.fromEnglish("benefits", item),
      onDelete: isReadOnly ? false : () => this._removeLsaItem(item),
      variant: "default",
      style: styles.chip
    })))), isAuthorityView && !isReadOnly && this.state.lsaModal && /*#__PURE__*/React.createElement(LsaModal, {
      open: Boolean(this.state.lsaModal),
      close: () => this._toggle("lsaModal"),
      onBack: () => this._toggle("lsaModal"),
      items: this.state.lsaItems,
      isMasterApp: true,
      onSave: this.saveLsaItems
    }), /*#__PURE__*/React.createElement(Grid, {
      item: true,
      sx: styles.gridStyle,
      xs: 12
    }, /*#__PURE__*/React.createElement(TextInput, {
      label: i18n.t(tokens.labels.masterApplication.notes),
      fullWidth: true,
      value: this.state.notes,
      variant: "outlined",
      onBlur: () => this._onBlur('notes'),
      onChange: this._handleChange('notes'),
      multiline: true,
      rows: 5
    })), /*#__PURE__*/React.createElement(React.Fragment, null, isAuthorityView && !isReadOnly && !this.state.bankingInstitutionName && this.props.currentAccountMeta.status !== 3 && /*#__PURE__*/React.createElement(Grid, {
      item: true,
      sx: styles.gridStyle,
      xs: 12
    }, /*#__PURE__*/React.createElement(Button, {
      style: styles.saveAndSendBtn,
      onClick: this._handleEditBankingDetails,
      color: "primary"
    }, this.state.eftDetails.default.accepted ? i18n.t(tokens.buttons.editBankingDetails) : i18n.t(tokens.buttons.addBankingDetails))), isAuthorityView && !isReadOnly && this.props.currentAccountMeta.status !== 3 && this.state.bankingModal && /*#__PURE__*/React.createElement(EditBankingDetails, {
      modalOpen: this.state.bankingModal,
      accountLevelBankingDetailsView: true,
      validateBankingDetails: this.props.validateBankingDetails,
      close: () => this._toggle("bankingModal"),
      saveNewBankingDetails: this._saveNewBankingDetails,
      closeModalAfterSaving: true,
      bankingDetailsVerifying: this.props.bankingDetailsVerifying
    }), this.state.eftDetails.default.accepted && this.props.currentAccountMeta.status !== 3 && /*#__PURE__*/React.createElement(DisplayBankingDetails, {
      accountHolder: this.state.eftDetails.default.accountHolder,
      accountNum: this.state.eftDetails.default.accountNum,
      institutionAddress: this.state.eftDetails.default.institutionAddress,
      institutionName: this.state.eftDetails.default.institutionName,
      institutionNum: this.state.eftDetails.default.institutionNum,
      transitNum: this.state.eftDetails.default.transitNum,
      accepted: this.state.eftDetails.default.accepted
    })), (this.props.isPrivateLabelAdvisor || this.props.currentAccountMeta.state >= 6) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Grid, {
      item: true,
      sx: styles.gridStyle,
      xs: 12,
      sm: 4
    }, /*#__PURE__*/React.createElement(TextInput, {
      label: i18n.t(tokens.labels.masterApplication.advisorSignature),
      fullWidth: true,
      required: true,
      value: this.state.signage.advisor.signature,
      onChange: e => this._handleSignageBlockChange('advisor', 'signature', e.target.value),
      inputProps: {
        inputProps: {
          style: {
            ...styles.signature
          }
        }
      },
      disabled: isAuthorityView,
      error: this.state.errors.advisorSignatureError,
      customStyle: styles.signStyle
    })), /*#__PURE__*/React.createElement(Grid, {
      item: true,
      sx: styles.gridStyle,
      xs: 12,
      sm: 4
    }, /*#__PURE__*/React.createElement(TextInput, {
      label: i18n.t(tokens.labels.masterApplication.advisorName),
      fullWidth: true,
      required: true,
      value: this.state.signage.advisor.name,
      onChange: e => this._handleSignageBlockChange('advisor', 'name', e.target.value),
      disabled: isAuthorityView,
      error: this.state.errors.advisorNameError,
      customStyle: styles.signStyle
    })), /*#__PURE__*/React.createElement(Grid, {
      item: true,
      sx: styles.gridStyle,
      xs: 12,
      sm: 4
    }, /*#__PURE__*/React.createElement(DateInput, {
      label: i18n.t(tokens.labels.masterApplication.advisorDatedOn),
      fullWidth: true,
      required: true,
      value: this.state.signage.advisor.datedOn,
      onChange: e => this._handleSignageBlockChange('advisor', 'datedOn', e.target.value),
      disabled: isAuthorityView,
      error: this.state.errors.advisorDatedOnError,
      customStyle: styles.signStyle
    })), /*#__PURE__*/React.createElement(Grid, {
      item: true,
      xs: 12,
      sx: styles.signageAcceptBox
    }, /*#__PURE__*/React.createElement(FormControl, {
      fullWidth: true
    }, /*#__PURE__*/React.createElement(FormGroup, {
      row: true
    }, /*#__PURE__*/React.createElement(FormControlLabel, {
      control: /*#__PURE__*/React.createElement(Checkbox, {
        checked: this.state.signage.advisor.acceptedTerms,
        onChange: () => this._handleSignageBlockChange('advisor', 'acceptedTerms', !this.state.signage.advisor.acceptedTerms),
        value: "advisorAcceptedTerms",
        disabled: isAuthorityView,
        color: "secondary"
      }),
      label: /*#__PURE__*/React.createElement(Text, {
        avMedium: true,
        color: "inherit"
      }, i18n.t(tokens.labels.masterApplication.advisorAcknowledgment))
    })), this.state.errors.advisorAcceptedTermsError && /*#__PURE__*/React.createElement(FormHelperText, {
      sx: {
        marginLeft: "0px"
      },
      error: true
    }, i18n.t(tokens.labels.banking.youMustAcceptTermsError)))), /*#__PURE__*/React.createElement(Grid, {
      item: true,
      sx: styles.gridStyle,
      xs: 12,
      sm: 4
    }, /*#__PURE__*/React.createElement(TextInput, {
      label: i18n.t(tokens.labels.masterApplication.authoritySignature),
      fullWidth: true,
      value: this.state.signage.authority.signature,
      onChange: e => this._handleSignageBlockChange('authority', 'signature', e.target.value),
      inputProps: {
        inputProps: {
          style: {
            ...styles.signature
          }
        }
      },
      disabled: !isAuthorityView || isReadOnly,
      error: this.state.errors.authoritySignatureError,
      customStyle: styles.signStyle
    })), /*#__PURE__*/React.createElement(Grid, {
      item: true,
      sx: styles.gridStyle,
      xs: 12,
      sm: 4
    }, /*#__PURE__*/React.createElement(TextInput, {
      label: i18n.t(tokens.labels.masterApplication.authorityName),
      fullWidth: true,
      value: this.state.signage.authority.name,
      onChange: e => this._handleSignageBlockChange('authority', 'name', e.target.value),
      disabled: !isAuthorityView || isReadOnly,
      error: this.state.errors.authorityNameError,
      customStyle: styles.signStyle
    })), /*#__PURE__*/React.createElement(Grid, {
      item: true,
      sx: styles.gridStyle,
      xs: 12,
      sm: 4
    }, /*#__PURE__*/React.createElement(DateInput, {
      label: i18n.t(tokens.labels.masterApplication.authorityDatedOn),
      fullWidth: true,
      value: this.state.signage.authority.datedOn,
      onChange: e => this._handleSignageBlockChange('authority', 'datedOn', e.target.value),
      disabled: !isAuthorityView || isReadOnly,
      error: this.state.errors.authorityDatedOnError,
      customStyle: styles.signStyle
    })), /*#__PURE__*/React.createElement(Grid, {
      item: true,
      xs: 12,
      sx: styles.signageAcceptBox
    }, /*#__PURE__*/React.createElement(FormControl, {
      fullWidth: true
    }, /*#__PURE__*/React.createElement(FormGroup, {
      row: true
    }, /*#__PURE__*/React.createElement(FormControlLabel, {
      control: /*#__PURE__*/React.createElement(Checkbox, {
        checked: this.state.signage.authority.acceptedTerms,
        onChange: () => this._handleSignageBlockChange('authority', 'acceptedTerms', !this.state.signage.authority.acceptedTerms),
        value: "authorityAcceptedTerms",
        disabled: !isAuthorityView || isReadOnly,
        color: "secondary"
      }),
      label: /*#__PURE__*/React.createElement(Text, {
        avMedium: true,
        color: "inherit"
      }, i18n.t(tokens.labels.masterApplication.authorityAcknowledgment))
    })), this.state.errors.authorityAcceptedTermsError && /*#__PURE__*/React.createElement(FormHelperText, {
      sx: {
        marginLeft: "0px"
      },
      error: true
    }, i18n.t(tokens.labels.banking.youMustAcceptTermsError)))), /*#__PURE__*/React.createElement(Grid, {
      item: true,
      sx: styles.gridStyle,
      xs: 12,
      sm: 4
    }, /*#__PURE__*/React.createElement(TextInput, {
      label: i18n.t(tokens.labels.masterApplication.witnessSignature),
      fullWidth: true,
      value: this.state.signage.witness.signature,
      onChange: e => this._handleSignageBlockChange('witness', 'signature', e.target.value),
      inputProps: {
        inputProps: {
          style: {
            ...styles.signature
          }
        }
      },
      disabled: !isAuthorityView || isReadOnly,
      error: this.state.errors.witnessSignatureError,
      customStyle: styles.signStyle
    })), /*#__PURE__*/React.createElement(Grid, {
      item: true,
      sx: styles.gridStyle,
      xs: 12,
      sm: 4
    }, /*#__PURE__*/React.createElement(TextInput, {
      label: i18n.t(tokens.labels.masterApplication.witnessName),
      fullWidth: true,
      value: this.state.signage.witness.name,
      onChange: e => this._handleSignageBlockChange('witness', 'name', e.target.value),
      disabled: !isAuthorityView || isReadOnly,
      error: this.state.errors.witnessNameError,
      customStyle: styles.signStyle
    })), /*#__PURE__*/React.createElement(Grid, {
      item: true,
      sx: styles.gridStyle,
      xs: 12,
      sm: 4
    }, /*#__PURE__*/React.createElement(DateInput, {
      label: i18n.t(tokens.labels.masterApplication.witnessDatedOn),
      fullWidth: true,
      value: this.state.signage.witness.datedOn,
      onChange: e => this._handleSignageBlockChange('witness', 'datedOn', e.target.value),
      disabled: !isAuthorityView || isReadOnly,
      error: this.state.errors.witnessDatedOnError,
      customStyle: styles.signStyle
    })), /*#__PURE__*/React.createElement(Grid, {
      item: true,
      xs: 12,
      sx: styles.signageAcceptBox
    }, /*#__PURE__*/React.createElement(FormControl, {
      fullWidth: true
    }, /*#__PURE__*/React.createElement(FormGroup, {
      row: true
    }, /*#__PURE__*/React.createElement(FormControlLabel, {
      control: /*#__PURE__*/React.createElement(Checkbox, {
        checked: this.state.signage.witness.acceptedTerms,
        onChange: () => this._handleSignageBlockChange('witness', 'acceptedTerms', !this.state.signage.witness.acceptedTerms),
        value: "witnessAcceptedTerms",
        disabled: !isAuthorityView || isReadOnly,
        color: "secondary"
      }),
      label: /*#__PURE__*/React.createElement(Text, {
        avMedium: true,
        color: "inherit"
      }, i18n.t(tokens.labels.masterApplication.witnessAcknowledgment))
    })), this.state.errors.witnessAcceptedTermsError && /*#__PURE__*/React.createElement(FormHelperText, {
      sx: {
        marginLeft: "0px"
      },
      error: true
    }, i18n.t(tokens.labels.banking.youMustAcceptTermsError)))))), !this.props.isPrivateLabelAdvisor && !isAuthorityView && this.props.currentAccountMeta.state <= 3 && /*#__PURE__*/React.createElement(Grid, {
      container: true,
      sx: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "flex-end",
        marginTop: 2
      }
    }, /*#__PURE__*/React.createElement(Button, {
      customStyle: styles.saveAndSendBtn,
      onClick: this._saveAndSubmit,
      color: "primary"
    }, i18n.t(tokens.buttons.saveAndSubmit)), /*#__PURE__*/React.createElement(Button, {
      onClick: () => this._save(true, true, true),
      color: "primary"
    }, i18n.t(tokens.buttons.save))), /*#__PURE__*/React.createElement(Modal, {
      open: Boolean(this.state.modalTypeOpen),
      onClose: this._closeModal,
      title: this.state.modalTitle,
      primaryButton: {
        onClick: this.state.isEdit ? this._updateArrayItem : this._addArrayItem
      },
      loader: this.props.loader
    }, this.state.modalTypeOpen === 'dependentsWithSurvivorBenefits' && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TextInput, {
      label: i18n.t(tokens.labels.masterApplication.dependentName),
      value: this.state.currentItem.name,
      onBlur: () => this._modalFormItemBlur('name'),
      onChange: this._handleModalFormItemChange('name'),
      error: Boolean(this.state.errors.currentModal?.nameError),
      fullWidth: true
    }), /*#__PURE__*/React.createElement(DateInput, {
      label: i18n.t(tokens.labels.masterApplication.expiryDate),
      value: this.state.currentItem.expiryDate,
      onBlur: () => this._onBlurDate('expiryDate', false, true),
      onChange: this._handleModalFormItemChange('expiryDate'),
      error: Boolean(this.state.errors.currentModal?.expiryDateError),
      fullWidth: true
    })), this.state.modalTypeOpen === 'employeesNotActivelyAtWork' && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TextInput, {
      label: i18n.t(tokens.labels.masterApplication.employeeName),
      value: this.state.currentItem.name,
      onBlur: () => this._modalFormItemBlur('name'),
      onChange: this._handleModalFormItemChange('name'),
      error: Boolean(this.state.errors.currentModal?.nameError),
      fullWidth: true
    }), /*#__PURE__*/React.createElement(DateInput, {
      label: i18n.t(tokens.labels.masterApplication.dateLastWorked),
      value: this.state.currentItem.dateLastAtWork,
      onBlur: () => this._onBlurDate('dateLastAtWork', true, true),
      onChange: this._handleModalFormItemChange('dateLastAtWork'),
      error: Boolean(this.state.errors.currentModal?.dateLastAtWorkError),
      fullWidth: true
    }), /*#__PURE__*/React.createElement(DateInput, {
      label: i18n.t(tokens.labels.masterApplication.expectedReturnDate),
      value: this.state.currentItem.expectedReturnDate,
      onBlur: () => this._onBlurDate('expectedReturnDate', false, true),
      onChange: this._handleModalFormItemChange('expectedReturnDate'),
      error: Boolean(this.state.errors.currentModal?.expectedReturnDateError),
      fullWidth: true
    }), /*#__PURE__*/React.createElement(TextInput, {
      label: i18n.t(tokens.labels.masterApplication.reason),
      value: this.state.currentItem.reasonNotAtWork,
      onBlur: () => this._modalFormItemBlur('reasonNotAtWork'),
      onChange: this._handleModalFormItemChange('reasonNotAtWork'),
      error: Boolean(this.state.errors.currentModal?.reasonNotAtWorkError),
      fullWidth: true
    }), /*#__PURE__*/React.createElement(TextInput, {
      label: i18n.t(tokens.labels.masterApplication.claimStatus),
      value: this.state.currentItem.claimStatus,
      onBlur: () => this._modalFormItemBlur('claimStatus'),
      onChange: this._handleModalFormItemChange('claimStatus'),
      error: Boolean(this.state.errors.currentModal?.claimStatusError),
      fullWidth: true
    })), this.state.modalTypeOpen === 'contractEmployees' && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TextInput, {
      label: i18n.t(tokens.labels.masterApplication.employeeName),
      value: this.state.currentItem.name,
      onBlur: () => this._modalFormItemBlur('name'),
      onChange: this._handleModalFormItemChange('name'),
      error: Boolean(this.state.errors.currentModal?.nameError),
      fullWidth: true
    }), /*#__PURE__*/React.createElement(TextInput, {
      label: i18n.t(tokens.labels.masterApplication.occupation),
      value: this.state.currentItem.occupation,
      onBlur: () => this._modalFormItemBlur('occupation'),
      onChange: this._handleModalFormItemChange('occupation'),
      error: Boolean(this.state.errors.currentModal?.occupationError),
      fullWidth: true
    }), /*#__PURE__*/React.createElement(DateInput, {
      label: i18n.t(tokens.labels.masterApplication.contractStartDate),
      value: this.state.currentItem.contractStartDate,
      onBlur: () => this._onBlurDate('contractStartDate', true, true),
      onChange: this._handleModalFormItemChange('contractStartDate'),
      error: Boolean(this.state.errors.currentModal?.contractStartDateError),
      fullWidth: true
    }), /*#__PURE__*/React.createElement(DateInput, {
      label: i18n.t(tokens.labels.masterApplication.contractEndDate),
      value: this.state.currentItem.contractEndDate,
      onBlur: () => this._onBlurDate('contractEndDate', false, true),
      onChange: this._handleModalFormItemChange('contractEndDate'),
      error: Boolean(this.state.errors.currentModal?.contractEndDateError),
      fullWidth: true
    })), this.state.modalTypeOpen === 'exclusionsFromCoverage' && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TextInput, {
      label: i18n.t(tokens.labels.masterApplication.employeeNameCategory),
      value: this.state.currentItem.name,
      onBlur: () => this._modalFormItemBlur('name'),
      onChange: this._handleModalFormItemChange('name'),
      error: Boolean(this.state.errors.currentModal?.nameError),
      fullWidth: true
    }), /*#__PURE__*/React.createElement(TextInput, {
      label: i18n.t(tokens.labels.masterApplication.reason),
      value: this.state.currentItem.reasonForExclusion,
      onBlur: () => this._modalFormItemBlur('reasonForExclusion'),
      onChange: this._handleModalFormItemChange('reasonForExclusion'),
      error: Boolean(this.state.errors.currentModal?.reasonForExclusionError),
      fullWidth: true
    })), this.state.modalTypeOpen === 'existingCoverage' && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TextInput, {
      label: i18n.t(tokens.labels.masterApplication.nameOfInsurerCarrier),
      value: this.state.currentItem.carrier,
      onBlur: () => this._modalFormItemBlur('carrier'),
      onChange: this._handleModalFormItemChange('carrier'),
      error: Boolean(this.state.errors.currentModal?.carrierError),
      fullWidth: true
    }), /*#__PURE__*/React.createElement(FormControl, {
      component: "fieldset",
      style: {
        marginTop: 20
      },
      error: Boolean(this.state.errors.currentModal?.benefitsError)
    }, /*#__PURE__*/React.createElement(FormLabel, {
      sx: styles.formLabel,
      component: "legend"
    }, i18n.t(tokens.labels.masterApplication.benefitsCovered), ":"), /*#__PURE__*/React.createElement(FormGroup, {
      row: true
    }, /*#__PURE__*/React.createElement(FormControlLabel, {
      control: /*#__PURE__*/React.createElement(Checkbox, {
        checked: this.state.currentItem.benefits.indexOf('Basic Life') > -1,
        onChange: this._handleModalFormItemChange('benefits', 'Basic Life'),
        value: "basicLife",
        disabled: isAuthorityView,
        color: "secondary"
      }),
      label: /*#__PURE__*/React.createElement(Text, {
        avMedium: true
      }, i18n.t(tokens.titles.basicLife))
    }), /*#__PURE__*/React.createElement(FormControlLabel, {
      control: /*#__PURE__*/React.createElement(Checkbox, {
        checked: this.state.currentItem.benefits.indexOf('AD&D') > -1,
        onChange: this._handleModalFormItemChange('benefits', 'AD&D'),
        value: "add",
        color: "secondary"
      }),
      label: /*#__PURE__*/React.createElement(Text, {
        avMedium: true
      }, i18n.t(tokens.titles["ad&d"]))
    }), /*#__PURE__*/React.createElement(FormControlLabel, {
      control: /*#__PURE__*/React.createElement(Checkbox, {
        checked: this.state.currentItem.benefits.indexOf('Dependent Life') > -1,
        onChange: this._handleModalFormItemChange('benefits', 'Dependent Life'),
        value: "dependentLife",
        disabled: isAuthorityView,
        color: "secondary"
      }),
      label: /*#__PURE__*/React.createElement(Text, {
        avMedium: true
      }, i18n.t(tokens.titles.dependentLife))
    }), /*#__PURE__*/React.createElement(FormControlLabel, {
      control: /*#__PURE__*/React.createElement(Checkbox, {
        checked: this.state.currentItem.benefits.indexOf('EHC') > -1,
        onChange: this._handleModalFormItemChange('benefits', 'EHC'),
        value: "ehc",
        disabled: isAuthorityView,
        color: "secondary"
      }),
      label: /*#__PURE__*/React.createElement(Text, {
        avMedium: true
      }, i18n.t(tokens.titles.ehc))
    }), /*#__PURE__*/React.createElement(FormControlLabel, {
      control: /*#__PURE__*/React.createElement(Checkbox, {
        checked: this.state.currentItem.benefits.indexOf('Dental') > -1,
        onChange: this._handleModalFormItemChange('benefits', 'Dental'),
        value: "dental",
        disabled: isAuthorityView,
        color: "secondary"
      }),
      label: /*#__PURE__*/React.createElement(Text, {
        avMedium: true
      }, i18n.t(tokens.titles.dental))
    }), /*#__PURE__*/React.createElement(FormControlLabel, {
      control: /*#__PURE__*/React.createElement(Checkbox, {
        checked: this.state.currentItem.benefits.indexOf('STD') > -1,
        onChange: this._handleModalFormItemChange('benefits', 'STD'),
        value: "std",
        disabled: isAuthorityView,
        color: "secondary"
      }),
      label: /*#__PURE__*/React.createElement(Text, {
        avMedium: true
      }, i18n.t(tokens.titles.std))
    }), /*#__PURE__*/React.createElement(FormControlLabel, {
      control: /*#__PURE__*/React.createElement(Checkbox, {
        checked: this.state.currentItem.benefits.indexOf('LTD') > -1,
        onChange: this._handleModalFormItemChange('benefits', 'LTD'),
        value: "ltd",
        disabled: isAuthorityView,
        color: "secondary"
      }),
      label: /*#__PURE__*/React.createElement(Text, {
        avMedium: true
      }, i18n.t(tokens.titles.ltd))
    })), this.state.errors.currentModal?.benefitsError && /*#__PURE__*/React.createElement(Text, {
      small: true,
      color: "#d93025",
      display: "block",
      align: "left"
    }, this.state.errors.currentModal.benefitsError)))), this.state.reviewRequestSentSuccessDialogOpen && /*#__PURE__*/React.createElement(StepCompletionNotif, {
      dialog: i18n.t(tokens.labels.masterApplication.submissionSuccess),
      type: i18n.t(tokens.titles.masterApplication),
      open: Boolean(this.state.reviewRequestSentSuccessDialogOpen),
      handleClose: this._closeReviewRequestSentSuccessDialog,
      handleNext: this._goToNextStep,
      disableBackdropClick: true,
      disableEscapeKeyDown: true
    }), this.state.modalTypeOpen === "checkBoxModal" && /*#__PURE__*/React.createElement(Modal, {
      open: this.state.modalTypeOpen === "checkBoxModal",
      onClose: () => this._toggleModal(false),
      title: "Please confirm changes.",
      primaryButton: {
        label: "Close",
        onClick: () => this._toggleModal(false)
      },
      loader: this.props.loader
    }, /*#__PURE__*/React.createElement(Text, {
      medium: true,
      light: true,
      display: "block",
      align: "left",
      margin: "20px 0"
    }, i18n.t(tokens.labels.signage.pleaseAcceptAndAcknowledgeChanges)))));
  }
}
export default MasterAppForm;